import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Calculator, Download } from "lucide-react";

export default function GST() {
  const [data, setData] = useState(null);
  const [amount, setAmount] = useState(1000);
  const [rate, setRate] = useState(18);
  const [inclusive, setInclusive] = useState(false);

  useEffect(() => {
    api.get("/reports/gst").then((r) => setData(r.data));
  }, []);

  let base = parseFloat(amount) || 0;
  let gstAmount;
  if (inclusive) {
    gstAmount = (base * rate) / (100 + parseFloat(rate));
    base = base - gstAmount;
  } else {
    gstAmount = (base * rate) / 100;
  }
  const total = base + gstAmount;

  return (
    <div className="space-y-6" data-testid="gst-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">GST</h1>
        <p className="text-sm text-muted-foreground mt-1">Tax summary, HSN report, GST calculator</p>
      </div>

      {data && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat label="Taxable Value" value={inr(data.summary.total_taxable)} />
          <Stat label="CGST" value={inr(data.summary.total_cgst)} />
          <Stat label="SGST" value={inr(data.summary.total_sgst)} />
          <Stat label="IGST" value={inr(data.summary.total_igst)} />
          <Stat label="Total Tax" value={inr(data.summary.total_tax)} highlight />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <div className="font-heading font-bold text-lg mb-4">HSN Summary</div>
          {!data || data.hsn_report.length === 0 ? (
            <div className="text-sm text-muted-foreground">No invoices yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>HSN</TableHead><TableHead>Qty</TableHead><TableHead>Taxable</TableHead><TableHead>Tax</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.hsn_report.map((h) => (
                  <TableRow key={h.hsn}>
                    <TableCell className="font-medium">{h.hsn}</TableCell>
                    <TableCell>{h.qty}</TableCell>
                    <TableCell>{inr(h.taxable)}</TableCell>
                    <TableCell>{inr(h.tax)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Card className="p-6">
          <div className="font-heading font-bold text-lg flex items-center gap-2 mb-4"><Calculator size={18} /> GST Calculator</div>
          <div className="space-y-3">
            <div>
              <Label>Amount</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="gst-calc-amount" />
            </div>
            <div>
              <Label>GST Rate %</Label>
              <select className="w-full mt-1 h-10 rounded-lg border bg-background px-3 text-sm" value={rate} onChange={(e) => setRate(parseFloat(e.target.value))} data-testid="gst-calc-rate">
                {[0, 3, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={inclusive} onChange={(e) => setInclusive(e.target.checked)} />
              Amount is GST inclusive
            </label>
            <div className="border-t pt-3 space-y-1.5 text-sm">
              <Row label="Base" value={inr(base)} />
              <Row label={`GST (${rate}%)`} value={inr(gstAmount)} />
              <Row label="CGST" value={inr(gstAmount / 2)} />
              <Row label="SGST" value={inr(gstAmount / 2)} />
              <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                <span>Total</span><span data-testid="gst-calc-total">{inr(total)}</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-6">
        <div className="font-heading font-bold text-lg mb-4">GSTR-1 / GSTR-3B Report</div>
        <div className="text-sm text-muted-foreground mb-4">Aggregated outward supplies, ready for upload to GSTN portal.</div>
        <Button variant="outline" onClick={() => window.print()}><Download size={14} className="mr-1" /> Export Report</Button>
      </Card>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <Card className={`p-5 ${highlight ? "bg-primary/5 border-primary/20" : ""}`}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-heading font-bold text-xl mt-1">{value}</div>
    </Card>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span>{value}</span></div>;
}
